import { ModalDialog, PrimaryButton, TextButton } from '@swaggerhub-portal/component-library'
import clsx from 'clsx'
import { Trans, useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'
import { z } from 'zod'

import appDefaults from '~/app.defaults.json'
import Form from '~/components/Form'
import { useSubmitting } from '~/hooks/useSubmitting'
import { ACTION_TYPE } from '~/services/actions'
import { LandingProduct } from '~/types/product'

const CREATE_REQUEST_ACCESS_ACTION = `/?action=${ACTION_TYPE.CREATE_REQUEST_ACCESS}`

export interface RequestAccessModalProps {
  modalOpen: boolean;
  setModalOpen: (value: boolean) => void;
  product: LandingProduct;
  userFullName: string;
  accentColor?: string;
}

export const RequestAccessModal = ({ modalOpen, setModalOpen, product, userFullName, accentColor }: RequestAccessModalProps) => {
  const { t } = useTranslation()

  const isDesktop = useMediaQuery(appDefaults.media.desktop, { initializeWithValue: false, defaultValue: true })

  const submitting = useSubmitting(CREATE_REQUEST_ACCESS_ACTION, () => {
    setModalOpen(false)
  })
  
  const schema = z.object({ note: z.string().max(appDefaults.requestAccessModal.notes.maxLength).default('') })

  return (
    <ModalDialog
      transition={isDesktop}
      closeOnOverlayClick={isDesktop}
      loading={submitting}
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      size="m"
      translations={{
        title: t('portal.landingPage.requestAccessModal.requestAccess.title'),
        description: <Trans t={t} i18nKey="portal.landingPage.requestAccessModal.requestAccess.subtitle" values={{ activeProduct: product.name }} />
      }}
    >
      <Form schema={schema} method="post" mode="onChange" action={CREATE_REQUEST_ACCESS_ACTION} >
        {({ Field, watch }) => {
          const watchNote = watch('note')

          return (
            <section className="flex flex-col gap-4">
              <Field
                name="note"
                placeholder={t('portal.landingPage.requestAccessModal.requestAccess.notes.placeholder')}
              >
                {({ Input, Label }) =>
                  <>
                    <Label className="font-semibold text-neutral-70 dark:text-neutral-40">
                      {t('portal.landingPage.requestAccessModal.requestAccess.notes.label')}
                    </Label>
                    <Input
                      className="peer block w-full mt-0.5! rounded border-0 py-2.5 pr-10 text-body2 text-neutral-70 ring-1 ring-inset ring-neutral-50 placeholder:text-neutral-50 focus:ring-neutral-50"
                      maxLength={appDefaults.requestAccessModal.notes.maxLength}
                    />
                    <div className="flex justify-end mt-1! text-body3 text-neutral-98 dark:text-neutral-40">
                      <p>{t('portal.landingPage.requestAccessModal.requestAccess.notes.counter.label')}</p>
                      <p className="ml-1 font-semibold">{watchNote.length} / {appDefaults.requestAccessModal.notes.maxLength}</p>
                    </div>
                  </>
                }
              </Field>
              <input type="hidden" name="fullName" value={userFullName} />
              <input type="hidden" name="productId" value={product.id} />
              <div className="flex justify-end gap-2">
                <TextButton
                  onClick={() => setModalOpen(false)}
                  size="m"
                  className="hover:!bg-neutral-80 hover:!text-neutral-00 dark:hover:!bg-neutral-30 dark:hover:!text-neutral-98"
                >
                  {t('components.ui.buttons.cancel.label')}
                </TextButton>
                <PrimaryButton
                  data-testid="consumer-request-access-confirm"
                  loading={submitting}
                  size="m"
                  type="submit"
                  className={clsx(
                    !submitting && 'border ml-2 duration-200 transition-color bg-transparent hover:bg-neutral-80 hover:!border-neutral-80 hover:!text-neutral-00 !text-neutral-98 ',
                    !submitting && 'dark:bg-transparent dark:!text-neutral-20 dark:hover:bg-neutral-30 dark:hover:!border-neutral-30 dark:hover:!text-neutral-98',
                    !accentColor && !submitting && '!border-accent-60',
                  )}
                  style={{ borderColor: accentColor }}
                >{t('components.ui.buttons.confirm.label')}</PrimaryButton>
              </div>
            </section>
          )
        }}
      </Form>
    </ModalDialog>
  )
}